import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, CheckCircle, Loader2, ShoppingCart, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchaseReconciliation = () => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [remarkFilter, setRemarkFilter] = useState('all');

  const handleFileChange = (fileNum, event) => {
    const file = event.target.files[0];
    if (file && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload only Excel files (.xlsx, .xls)');
      return;
    }
    if (fileNum === 1) setFile1(file);
    else setFile2(file);
  };

  const handleAnalyze = async () => {
    if (!file1 || !file2) {
      toast.error('Please upload both files');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);

    try {
      const response = await axios.post(`${API}/reconciliation/purchase`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
      toast.success('Analysis completed and saved successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze files');
    } finally {
      setLoading(false);
    }
  };

  const getRemarkBadge = (remark) => {
    if (remark.includes('Found in')) return <Badge variant="default" className="bg-green-500">✓ Found</Badge>;
    if (remark.includes('Not found')) return <Badge variant="destructive">✗ Not Found</Badge>;
    if (remark.includes('differ')) return <Badge variant="secondary">⚠ Differs</Badge>;
    if (remark.includes('different GSTIN')) return <Badge variant="outline">⚠ GSTIN Mismatch</Badge>;
    return <Badge variant="outline">{remark}</Badge>;
  };

  const getRemarkCategory = (remark) => {
    if (remark.includes('Found in')) return 'found';
    if (remark.includes('Not found')) return 'not_found';
    if (remark.includes('differ')) return 'differs';
    if (remark.includes('different GSTIN')) return 'gstin_mismatch';
    return 'other';
  };

  // Filter function
  const filterRecords = useCallback((records) => {
    if (!records) return [];
    
    return records.filter(row => {
      // Search filter
      const matchesSearch = !searchTerm || 
        row['Supplier Name']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row['GSTIN']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row['Invoice No']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row['Remark']?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Remark filter
      const remarkCategory = getRemarkCategory(row['Remark']);
      const matchesRemark = remarkFilter === 'all' || remarkCategory === remarkFilter;
      
      return matchesSearch && matchesRemark;
    });
  }, [searchTerm, remarkFilter]);

  const filteredPurchaseRegister = useMemo(() => 
    filterRecords(results?.['Purchase Register']), 
    [results, filterRecords]
  );

  const filteredGSTR2B = useMemo(() => 
    filterRecords(results?.['GSTR2B']), 
    [results, filterRecords]
  );

  return (
    <div className="space-y-6" data-testid="purchase-reconciliation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <ShoppingCart className="h-8 w-8 mr-3 text-blue-600" />
            Purchase Reconciliation
          </h1>
          <p className="text-gray-600 mt-2">
            Compare Purchase Register with GSTR2B to identify discrepancies
          </p>
        </div>
      </div>

      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Files</CardTitle>
          <CardDescription>
            Upload your Purchase Register and GSTR2B files for reconciliation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="file1">Purchase Register (.xlsx)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file1"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileChange(1, e)}
                  className="cursor-pointer"
                  data-testid="purchase-register-file-input"
                />
                {file1 && <CheckCircle className="h-5 w-5 text-green-600" />}
              </div>
              {file1 && <p className="text-sm text-gray-600">{file1.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="file2">GSTR2B (.xlsx)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file2"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileChange(2, e)}
                  className="cursor-pointer"
                  data-testid="gstr2b-file-input"
                />
                {file2 && <CheckCircle className="h-5 w-5 text-green-600" />}
              </div>
              {file2 && <p className="text-sm text-gray-600">{file2.name}</p>}
            </div>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={!file1 || !file2 || loading}
            className="mt-6 w-full md:w-auto"
            data-testid="analyze-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Start Reconciliation
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section with Filters */}
      {results && (
        <>
          {/* Filters Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by Supplier, GSTIN, Invoice No..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                <Select value={remarkFilter} onValueChange={setRemarkFilter}>
                  <SelectTrigger className="w-48" data-testid="remark-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Remarks</SelectItem>
                    <SelectItem value="found">Found</SelectItem>
                    <SelectItem value="not_found">Not Found</SelectItem>
                    <SelectItem value="differs">Differs</SelectItem>
                    <SelectItem value="gstin_mismatch">GSTIN Mismatch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Results Card */}
          <Card>
            <CardHeader>
              <CardTitle>Reconciliation Results</CardTitle>
              <CardDescription>
                Review the comparison between Purchase Register and GSTR2B
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="purchase" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="purchase">
                    Purchase Register ({filteredPurchaseRegister.length} of {results['Purchase Register']?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="gstr2b">
                    GSTR2B ({filteredGSTR2B.length} of {results['GSTR2B']?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="purchase" className="mt-4">
                  <div className="rounded-md border overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Supplier Name</TableHead>
                          <TableHead>GSTIN</TableHead>
                          <TableHead>Invoice No</TableHead>
                          <TableHead>Invoice Date</TableHead>
                          <TableHead>Taxable Value</TableHead>
                          <TableHead>Total GST</TableHead>
                          <TableHead>Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPurchaseRegister.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                              No records match the current filters
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredPurchaseRegister.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{row['Supplier Name']}</TableCell>
                              <TableCell>{row['GSTIN']}</TableCell>
                              <TableCell>{row['Invoice No']}</TableCell>
                              <TableCell>{row['Invoice Date']}</TableCell>
                              <TableCell>{row['Taxable Value']}</TableCell>
                              <TableCell>{row['Total GST']}</TableCell>
                              <TableCell>{getRemarkBadge(row['Remark'])}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                <TabsContent value="gstr2b" className="mt-4">
                  <div className="rounded-md border overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Supplier Name</TableHead>
                          <TableHead>GSTIN</TableHead>
                          <TableHead>Invoice No</TableHead>
                          <TableHead>Invoice Date</TableHead>
                          <TableHead>Taxable Value</TableHead>
                          <TableHead>Total GST</TableHead>
                          <TableHead>Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredGSTR2B.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                              No records match the current filters
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredGSTR2B.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{row['Supplier Name']}</TableCell>
                              <TableCell>{row['GSTIN']}</TableCell>
                              <TableCell>{row['Invoice No']}</TableCell>
                              <TableCell>{row['Invoice Date']}</TableCell>
                              <TableCell>{row['Taxable Value']}</TableCell>
                              <TableCell>{row['Total GST']}</TableCell>
                              <TableCell>{getRemarkBadge(row['Remark'])}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PurchaseReconciliation;
